import {
  getFullStatValue,
  getStatValue
} from '@frontend/core/lib/domains/reporting'
import {
  ErrorBoundary,
  useAllPlayersSessionSummary,
  useSessionPlayers
} from '@frontend/react'
import {
  Card,
  GroupRadioToolbar,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMetricCell,
  TableRow,
  ToggleGroup,
  ToggleGroupItem
} from '@frontend/ui'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CompareTable } from '../../components/compare-table'
import { ExportFooter } from '../../components/export-footer'
import { ExportHeader } from '../../components/export-header'
import { PositionBadge } from '../../components/position-badge'
import { StatsTablePagination } from '../../components/stats-table-pagination'
import { TeamStatsFilterBar } from '../../components/team-stats-filter-bar'
import { compareBarCategories } from '../../lib/constants'
import { useRegisterExport } from '../../lib/export-context'
import { formatStatValue, getColumnUnitLabel } from '../../lib/format-cell'
import { useReportingState } from '../../lib/reporting-state'
import type { ReportingUnitType } from '../../lib/sport-config/types'
import { useReportingUnitSystem } from '../../lib/use-reporting-unit-system'
import { filterByPosition } from '../player-detail/formatter'
import type { SortDir, StatInput } from './helpers'
import { sortPlayerRows } from './helpers'
import { TeamStatsPrint } from './index.print'

// Match Figma: 57px body rows.
const ROW_HEIGHT_PX = 57
const MIN_PAGE_SIZE = 8

// Shared column widths between the body table (scrolls/fills available space)
// and the pinned totals table (sits above pagination). Both tables use
// `table-fixed` with this colgroup so columns line up pixel-for-pixel.
function PlayerTableCols({ metricCount }: { metricCount: number }) {
  return (
    <colgroup>
      <col style={{ width: '48px' }} />
      <col style={{ width: '160px' }} />
      <col style={{ width: '96px' }} />
      {Array.from({ length: metricCount }).map((_, i) => (
        <col key={i} />
      ))}
    </colgroup>
  )
}

export function TeamStatsView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    teamId,
    sessionId,
    team,
    session,
    sport,
    config: sportConfig,
    isLoading: contextLoading
  } = useReportingState()
  const unitSystem = useReportingUnitSystem(sport)
  const tabGroups = sportConfig.teamTable.tabGroups

  useRegisterExport(
    teamId && sessionId
      ? {
          sessionId,
          sessionDate: session?.date ?? '',
          sessionName: session?.name ?? '',
          sport
        }
      : null,
    [teamId, sessionId, session?.date, session?.name, sport]
  )

  // URL-tied state derived during render — stale keys from another sport
  // (e.g. soccer's `viewMode=technical` on AMF) auto-resolve to the first
  // valid tab so the table renders config-driven columns.
  const urlViewMode = searchParams.get('viewMode') ?? ''
  const viewMode = tabGroups.some((g) => g.key === urlViewMode)
    ? urlViewMode
    : (tabGroups[0]?.key ?? '')
  const activeGroup = tabGroups.find((g) => g.key === viewMode)
  const urlCategory = searchParams.get('category') ?? ''
  const activeCategory = activeGroup?.tabs.some((t) => t.key === urlCategory)
    ? urlCategory
    : (activeGroup?.tabs[0]?.key ?? '')
  const partitionId = searchParams.get('partitionId') ?? 'all'
  const position = searchParams.get('position') ?? 'All'

  const [sortField, setSortField] = useState<string>('number')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE)

  // Size the page to fill the flex-1 rows area. Measurement runs via both
  // useLayoutEffect (first paint + every render while loading state settles)
  // and a ResizeObserver (subsequent viewport changes). Uses a functional
  // setter so unchanged measurements don't trigger extra renders.
  const rowsAreaRef = useRef<HTMLDivElement>(null)
  const measurePageSize = useCallback(() => {
    const area = rowsAreaRef.current
    if (!area) return
    const height = area.clientHeight
    if (height <= 0) return
    const fit = Math.floor(height / ROW_HEIGHT_PX)
    const next = Math.max(MIN_PAGE_SIZE, fit)
    setPageSize((prev) => (prev === next ? prev : next))
  }, [])
  useLayoutEffect(() => {
    measurePageSize()
  })
  useEffect(() => {
    const area = rowsAreaRef.current
    if (!area) return
    const ro = new ResizeObserver(measurePageSize)
    ro.observe(area)

    return () => ro.disconnect()
  }, [measurePageSize])

  const updateParam = useCallback(
    (
      key: string,
      value: string | null,
      extras?: Record<string, string | null>
    ) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value == null || value === '') next.delete(key)
        else next.set(key, value)
        if (extras) {
          for (const [k, v] of Object.entries(extras)) {
            if (v == null || v === '') next.delete(k)
            else next.set(k, v)
          }
        }

        return next
      })
    },
    [setSearchParams]
  )

  const handleViewModeChange = (mode: string) => {
    if (!mode) return
    const group = tabGroups.find((g) => g.key === mode)
    updateParam('viewMode', mode, {
      category: group?.tabs[0]?.key ?? null
    })
  }
  const setActiveCategory = (cat: string) => {
    if (!cat) return
    updateParam('category', cat)
  }
  const setPartitionId = (id: string) =>
    updateParam('partitionId', id === 'all' ? null : id)
  const setPosition = (pos: string) =>
    updateParam('position', pos === 'All' ? null : pos)

  const { list: players, isLoading: playersLoading } = useSessionPlayers(
    teamId,
    sessionId
  )
  const activePeriodId = partitionId === 'all' ? undefined : partitionId
  const activePosition = position === 'All' ? undefined : position
  const {
    playerStats,
    totalStats,
    isLoading: statsLoading,
    isFetching: statsFetching
  } = useAllPlayersSessionSummary(
    teamId,
    sessionId,
    activePeriodId,
    activePosition
  )

  const activeColumns = useMemo(() => {
    for (const group of tabGroups) {
      for (const tab of group.tabs) {
        if (tab.key === activeCategory) {
          return tab.columns.map((col) => ({
            key: col.accessor,
            label: col.label,
            unitType: col.unitType,
            fixedUnitNote: col.fixedUnitNote
          }))
        }
      }
    }
    const firstStats = playerStats[0]?.stats
    if (!firstStats) return []

    return Object.keys(firstStats).map((key) => ({
      key,
      label: key,
      unitType: undefined as ReportingUnitType | undefined,
      fixedUnitNote: undefined as string | undefined
    }))
  }, [activeCategory, tabGroups, playerStats])

  useEffect(() => {
    setSortField('number')
    setSortDir('asc')
  }, [activeCategory])

  const playerRows = useMemo(() => {
    const statsMap = new Map(playerStats.map((s) => [s.playerId, s.stats]))
    const positionFiltered = filterByPosition(
      players,
      activePosition ?? '',
      sportConfig
    )
    let rows = positionFiltered
      .map((player) => ({
        player,
        stats: statsMap.get(player.id)
      }))
      .filter((r) => r.player != null)

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      rows = rows.filter(({ player }) => {
        const name =
          player.displayName || `${player.firstName} ${player.lastName}`

        return (
          name.toLowerCase().includes(q) ||
          player.firstName?.toLowerCase().includes(q) ||
          player.lastName?.toLowerCase().includes(q)
        )
      })
    }

    return sortPlayerRows(rows, sortField, sortDir, sportConfig)
  }, [
    players,
    playerStats,
    activePosition,
    searchQuery,
    sortField,
    sortDir,
    sportConfig
  ])

  // Pagination — only shown when rows exceed one page. Reset to page 0 when
  // any filter or the visible row set changes so users never land empty.
  const pageCount = Math.ceil(playerRows.length / pageSize)
  useEffect(() => {
    setPage(0)
  }, [
    playerRows.length,
    activeCategory,
    viewMode,
    position,
    partitionId,
    searchQuery,
    pageSize
  ])
  const paginatedRows = useMemo(() => {
    if (playerRows.length <= pageSize) return playerRows

    return playerRows.slice(page * pageSize, (page + 1) * pageSize)
  }, [playerRows, page, pageSize])

  const isLoading = contextLoading || playersLoading || statsLoading
  const isRefetching = statsFetching && !statsLoading

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const handleSortKeyDown = (field: string, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleSort(field)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null

    return sortDir === 'asc' ? (
      <ChevronUp className='inline h-3 w-3' />
    ) : (
      <ChevronDown className='inline h-3 w-3' />
    )
  }

  const isTraining = session?.type?.toLowerCase() === 'training'
  const partitionAllLabel = isTraining
    ? 'All drills'
    : `All ${sportConfig.periodPluralLabel.toLowerCase()}`

  const partitionOptions = useMemo(() => {
    if (!session) return []
    if (session.drills?.length) {
      return session.drills.map((d) => ({ id: d.id, name: d.name }))
    }
    if (session.periods?.length) {
      return session.periods.map((p) => ({
        id: p.id,
        name: sportConfig.formatPeriodName(p.periodNumber ?? 1, p.overtime)
      }))
    }

    return []
  }, [session, sportConfig])

  const positionOptions = useMemo(
    () =>
      sportConfig.positionFilters.map((f) => ({
        value: f.value,
        label: f.label
      })),
    [sportConfig.positionFilters]
  )

  if (isLoading) {
    return (
      <div className='space-y-6 px-4 py-6'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-[600px] w-full' />
      </div>
    )
  }

  if (!team || !session) {
    return (
      <div className='flex h-full items-center justify-center p-8'>
        <p className='text-muted-foreground text-lg'>Session not found.</p>
      </div>
    )
  }

  return (
    <>
      <ExportHeader
        teamName={team.name}
        title='Team View'
        sessionType={session.type}
        sessionName={session.name}
        partitionName={
          partitionId === 'all'
            ? 'All'
            : partitionOptions.find((p) => p.id === partitionId)?.name
        }
      />
      <div className='flex h-[calc(100vh-65px)] flex-col gap-6 overflow-hidden px-4 py-6 print:hidden'>
        <TeamStatsFilterBar
          positionOptions={positionOptions}
          position={position}
          onPositionChange={setPosition}
          partitionAllLabel={partitionAllLabel}
          partitionOptions={partitionOptions}
          partitionId={partitionId}
          onPartitionChange={setPartitionId}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        <div className='flex min-h-0 flex-1 flex-col'>
          <ErrorBoundary
            useUIFallback
            fallbackTitle='Failed to load player stats'
          >
            <Card className='flex h-full flex-col gap-0 p-4'>
              <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
                {/* Hide the Physical/Technical pill pair when a sport only
                    has a single tab group (AMF / CFL right now). The
                    sub-tab ToggleGroup below slides into the left
                    position via justify-between's single-child
                    behaviour. */}
                {tabGroups.length > 1 && (
                  <GroupRadioToolbar
                    groups={tabGroups.map((g) => ({
                      key: g.key,
                      label: g.label
                    }))}
                    activeKey={viewMode}
                    onChange={handleViewModeChange}
                  />
                )}
                <ToggleGroup
                  type='single'
                  variant='outline'
                  value={activeCategory}
                  onValueChange={setActiveCategory}
                >
                  {(activeGroup?.tabs ?? []).map((t) => (
                    <ToggleGroupItem
                      key={t.key}
                      value={t.key}
                      className='min-w-[100px] px-4'
                    >
                      {t.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>

              <div className='relative flex min-h-0 flex-1 flex-col'>
                {isRefetching && (
                  <div
                    className='bg-background/50 absolute inset-0 z-10 flex items-center justify-center'
                    aria-live='polite'
                  >
                    <Loader2 className='text-muted-foreground h-8 w-8 animate-spin' />
                  </div>
                )}

                {activeCategory === 'compare' ? (
                  // Compare reuses the same rounded-lg card shell as the
                  // paginated stats table so the two sub-tab variants share
                  // visual framing — filter bar + toolbar stay pinned, the
                  // bar list scrolls internally.
                  <div className='border-border bg-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border'>
                    <CompareTable
                      playerRows={playerRows}
                      teamId={teamId}
                      sessionId={sessionId}
                      sortField={sortField}
                      sortDir={sortDir}
                      toggleSort={toggleSort}
                      onIsolateCategory={(id) => {
                        const cat = compareBarCategories.find(
                          (c) => c.id === id
                        )
                        if (!cat) return
                        setSortField(cat.statKey)
                        setSortDir('desc')
                      }}
                    />
                  </div>
                ) : (
                  <div className='border-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border'>
                    <div
                      ref={rowsAreaRef}
                      className='min-h-0 flex-1 overflow-x-auto overflow-y-hidden'
                    >
                      <Table className='table-fixed'>
                        <PlayerTableCols metricCount={activeColumns.length} />
                        <TableHeader>
                          <TableRow className='border-border hover:bg-transparent'>
                            <TableHead
                              className='text-muted-foreground w-12 cursor-pointer'
                              tabIndex={0}
                              onClick={() => toggleSort('number')}
                              onKeyDown={(e) => handleSortKeyDown('number', e)}
                            >
                              # <SortIcon field='number' />
                            </TableHead>
                            <TableHead
                              className='text-muted-foreground w-40 cursor-pointer'
                              tabIndex={0}
                              onClick={() => toggleSort('player')}
                              onKeyDown={(e) => handleSortKeyDown('player', e)}
                            >
                              Player <SortIcon field='player' />
                            </TableHead>
                            <TableHead
                              className='text-muted-foreground w-24 cursor-pointer'
                              tabIndex={0}
                              onClick={() => toggleSort('position')}
                              onKeyDown={(e) =>
                                handleSortKeyDown('position', e)
                              }
                            >
                              Pos. <SortIcon field='position' />
                            </TableHead>
                            {activeColumns.map((col) => {
                              const unit = getColumnUnitLabel(
                                col.unitType,
                                unitSystem
                              )

                              return (
                                <TableHead
                                  key={col.key}
                                  className='text-muted-foreground cursor-pointer text-right whitespace-pre-line'
                                  tabIndex={0}
                                  title={col.fixedUnitNote}
                                  onClick={() => toggleSort(col.key)}
                                  onKeyDown={(e) =>
                                    handleSortKeyDown(col.key, e)
                                  }
                                >
                                  {col.label}
                                  {unit ? ` (${unit})` : ''}
                                  {col.fixedUnitNote ? ' *' : ''}{' '}
                                  <SortIcon field={col.key} />
                                </TableHead>
                              )
                            })}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedRows.map(({ player, stats }) => {
                            if (!player) return null

                            return (
                              <TableRow
                                key={player.id}
                                className='border-border h-[57px]'
                              >
                                <TableCell className='text-foreground font-medium tabular-nums'>
                                  {player.number}
                                </TableCell>
                                <TableCell className='text-foreground font-medium'>
                                  <Link
                                    to={`/player/${player.id}?team=${teamId}&session=${sessionId}`}
                                    className='hover:underline'
                                  >
                                    {player.displayName ||
                                      `${player.firstName} ${player.lastName}`}
                                  </Link>
                                </TableCell>
                                <TableCell>
                                  <PositionBadge position={player.position} />
                                </TableCell>
                                {activeColumns.map((col) => {
                                  const playerStats = stats as
                                    | Record<string, unknown>
                                    | undefined
                                  const raw = playerStats?.[
                                    col.key
                                  ] as StatInput
                                  const numValue = getStatValue(raw)
                                  const fullStat = getFullStatValue(raw)
                                  if (!stats || numValue == null) {
                                    return (
                                      <TableCell
                                        key={col.key}
                                        className='text-foreground text-right tabular-nums'
                                      >
                                        -
                                      </TableCell>
                                    )
                                  }
                                  // `conv` feeds the bar geometry only (numeric
                                  // value/max/marker). The displayed text comes
                                  // from `formatStatValue`, which converts +
                                  // formats per the count/unit/plain rule.
                                  const conv =
                                    col.unitType && col.unitType !== 'count'
                                      ? (unitSystem.units[col.unitType]
                                          ?.conversion ?? 1)
                                      : 1
                                  const text = formatStatValue(
                                    numValue,
                                    col.unitType,
                                    unitSystem
                                  )
                                  if (fullStat) {
                                    return (
                                      <TableMetricCell
                                        key={col.key}
                                        className='text-foreground text-right tabular-nums'
                                        value={numValue * conv}
                                        maxValue={fullStat.maxValue * conv}
                                        markerValue={fullStat.seasonAvg * conv}
                                        valueLabel={text}
                                        maxLabel={formatStatValue(
                                          fullStat.maxValue,
                                          col.unitType,
                                          unitSystem
                                        )}
                                        markerLabel={formatStatValue(
                                          fullStat.seasonAvg,
                                          col.unitType,
                                          unitSystem
                                        )}
                                      >
                                        {text}
                                      </TableMetricCell>
                                    )
                                  }

                                  return (
                                    <TableCell
                                      key={col.key}
                                      className='text-foreground text-right tabular-nums'
                                    >
                                      {text}
                                    </TableCell>
                                  )
                                })}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    {/* Pinned totals row — plain <table> (not Table primitive)
                      so it doesn't fight the rows area for flex height. Uses
                      the same table-fixed + colgroup as the body table so
                      column widths align pixel-for-pixel. */}
                    <table className='bg-secondary/50 border-border w-full table-fixed border-t text-sm'>
                      <PlayerTableCols metricCount={activeColumns.length} />
                      <tbody>
                        <TableRow className='border-border hover:bg-transparent'>
                          <TableCell
                            className='text-foreground font-semibold'
                            colSpan={3}
                          >
                            Totals
                          </TableCell>
                          {activeColumns.map((col) => {
                            const totals = totalStats as
                              | Record<string, unknown>
                              | undefined
                            const numValue = getStatValue(
                              totals?.[col.key] as StatInput
                            )

                            return (
                              <TableCell
                                key={col.key}
                                className='text-foreground text-right font-semibold tabular-nums'
                              >
                                {formatStatValue(
                                  numValue,
                                  col.unitType,
                                  unitSystem
                                )}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {activeCategory !== 'compare' && pageCount > 1 && (
                <StatsTablePagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                />
              )}
            </Card>
          </ErrorBoundary>
        </div>
      </div>
      <TeamStatsPrint
        playerRows={playerRows}
        activeColumns={activeColumns}
        totalStats={totalStats}
        activeCategory={activeCategory}
        viewModeLabel={tabGroups.find((g) => g.key === viewMode)?.label}
        categoryLabel={
          tabGroups.flatMap((g) => g.tabs).find((t) => t.key === activeCategory)
            ?.label
        }
      />
      <ExportFooter />
    </>
  )
}
